import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import {
  MAX_LENGTH
} from '../../../constants/constants'
import { InputMasked } from '../../../ui'
import { isEmpty, map } from 'ramda'
import { getUserValue } from '../utils'
import { usePresetValue } from '../../utils/hooks'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const getMaskByName = (name) => {
  let mask
  switch (name) {
    case 'bsbNumber':
      mask = [/[1-9]/, /\d/, /\d/, '-', /\d/, /\d/, /\d/]
      break
    case 'branchNumber':
      mask = [/[1-9]/, /\d/, /\d/, /\d/]
      break
    case 'transitNumber':
      mask = [/[1-9]/, /\d/, /\d/, /\d/, /\d/]
      break
    case 'fractionalPrecision':
      mask = [/^([1-8])$/]
      break
    case 'fundPerformFee':
    case 'fundMgmtFee':
    case 'commissionAmount':
      mask = createNumberMask({
        prefix: '',
        allowDecimal: true,
        includeThousandsSeparator: false
      })
      break
  }
  return mask
}

const FEE_MAX_VALUE = 100

const ExternalMaskedText = ({ i18nLabel, type, name, required, maxLength, info }) => {
  const { t } = useTranslation()
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [error, setError] = useState('')
  const [{ form: { accId }, user }, dispatch] = useStore()
  const userValue = getUserValue(accId, user, name)

  const mask = useMemo(() => getMaskByName(name), [name])
  const translatedInfo = useMemo(() => info ? t(`generatedField.info.${info}`) : '', [info, t])

  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(value)
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  usePresetValue(name, userValue, presetValue)

  const onChange = ({ target: { value } }) => {
    setValue(value)
  }

  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    let isValid
    if (!required) {
      isValid = !trimmedValue.length || (trimmedValue.length && getIsValid(trimmedValue))
    } else {
      isValid = getIsValid(trimmedValue)
    }

    if (name === 'fundPerformFee' || name === 'fundMgmtFee') {
      if (+value > FEE_MAX_VALUE) {
        isValid = false
        setError(t('generatedField.maxValueError'))
      } else {
        setError('')
      }
    }

    if (isValid) {
      setIsValid(true)
      if (name === 'bsbNumber') {
        const val = value.split('')
        val.splice(3, 1)
        value = val.join('')
      }

      const checkValue = !required && isEmpty(value) ? null : +value.replace('-', '')
      dispatch({ type: 'setExtFieldValue', name, value: checkValue })
    } else {
      setIsValid(false)
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  const getIsValid = (value) => {
    return !!value.toString().length
  }

  return (
    <InputMasked
      name={name}
      label={label}
      value={value}
      type={type}
      mask={mask}
      guide={false}
      key={name}
      maxLength={maxLength || MAX_LENGTH}
      error={error}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      info={translatedInfo}
    />
  )
}

const getExternalMaskedTextFields = map(textField => {
  const { i18n, name, required, type, order, maxLength, info } = textField
  return (
    <ExternalMaskedText
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
      maxLength={maxLength}
      info={info}
    />
  )
})

export { ExternalMaskedText, getExternalMaskedTextFields }
